// ProductDetailComponent.jsx
import React, { useEffect, useMemo, useState , useRef } from "react";
import {
    Alert,
    AppBar,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Skeleton,
    Snackbar,
    Toolbar,
    Typography,
    ButtonBase,           // ✨ added
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerifiedIcon from "@mui/icons-material/Verified";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined"; // ✨ added
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";               // ✨ added
import { Link, useNavigate } from "react-router-dom";
import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import { BASE_URL_API, PRODUCT_BASE_URL_API } from "../Const";
import { CardShopRepository } from "../Repository/CardShopRepository";
import { BasketComponent } from "./BasketComponent";
import JsBarcode from "jsbarcode";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { QRCodeSVG } from "qrcode.react";

/* ---------- helpers ---------- */
const formatPrice = (val) => Number(val || 0).toLocaleString("fa-IR");
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const pill = (bg, color) => ({
    px: 1,
    py: 0.25,
    fontSize: 12,
    height: 22,
    lineHeight: 1.1,
    borderRadius: 1,
    bgcolor: bg,
    color,
    "& .MuiChip-icon": { fontSize: 16 },
});

/* ---------- inline Market card (clickable) ---------- */
function MarketLinkCardInline({ markets }) {
    const id = markets?.id ?? 4889;
    const to = `/market?id=${id}`;
    const logo =
        markets?.logo ? `${PRODUCT_BASE_URL_API}${markets.logo}` : "https://hamekara.com/hamekara-logo.png";

    return (
        <ButtonBase
            component={Link}
            to={to}
            sx={{ width: "100%", textAlign: "inherit", borderRadius: 2 }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.08)",
                    bgcolor: "#fff",
                    transition: "transform 160ms ease, box-shadow 160ms ease",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
                    "&:active": { transform: "translateY(0)", boxShadow: 2 },
                }}
            >
                {/* Top stripe */}

                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        pt: 2,
                        direction: "rtl",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Avatar
                        alt={markets?.name}
                        src={logo}
                        sx={{
                            width: 48,
                            height: 48,
                            border: "2px solid #fff",
                            mt: -4,
                            boxShadow: 1,
                            bgcolor: "#fff",
                        }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="subtitle1"
                            noWrap
                            title={markets?.name}
                            sx={{ fontWeight: 800 }}
                        >
                            {markets?.name}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                mt: 0.5,
                                flexWrap: "wrap",
                            }}
                        >
                            <Chip
                                icon={<VerifiedIcon />}
                                label="فروشنده تأیید‌شده"
                                size="small"
                                sx={{
                                    px: 0.75,
                                    height: 24,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,200,83,.1)",
                                    color: "success.main",
                                    "& .MuiChip-icon": { fontSize: 16 },
                                }}
                            />
                            <Chip
                                icon={<StorefrontOutlinedIcon />}
                                label="مشاهده فروشگاه"
                                size="small"
                                sx={{
                                    px: 0.75,
                                    height: 24,
                                    borderRadius: 1,
                                    bgcolor: "grey.100",
                                    color: "text.secondary",
                                    "& .MuiChip-icon": { fontSize: 16 },
                                }}
                            />
                        </Box>
                    </Box>
                    {/* Chevron (RTL) */}
                    <ChevronLeftIcon sx={{ color: "text.disabled" }} />
                </Box>

                <Divider sx={{ mx: 2 }} />

                {/* Address (non-blocking if empty) */}
                {markets?.address ? (
                    <Box
                        sx={{
                            p: 2,
                            pt: 1.25,
                            direction: "rtl",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <RoomOutlinedIcon fontSize="small" />
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                            noWrap
                            title={markets.address}
                        >
                            {markets.address}
                        </Typography>
                    </Box>
                ) : null}

                {/* Actions (note: outer ButtonBase makes whole card clickable) */}
                <Box
                    sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        direction: "rtl",
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                    }}
                >
                    {markets?.whatsapp && markets.whatsapp !== "none" && (
                        <Button
                            variant="outlined"
                            startIcon={<WhatsAppIcon />}
                            size="small"
                            onClick={(e) => {
                                e.preventDefault(); // keep routing intact if not clicked
                                e.stopPropagation();
                                window.open(`https://wa.me/${markets.whatsapp}`, "_blank");
                            }}
                            sx={{
                                gap: 1,
                                borderColor: "#25D366",
                                borderRadius: "12px",
                                color: "#25D366",
                                fontWeight: 600,
                                "& .MuiButton-startIcon": { color: "#25D366" },
                                "&:hover": {
                                    borderColor: "#1DA851",
                                    backgroundColor: "rgba(37,211,102,0.08)",
                                    color: "#1DA851",
                                    "& .MuiButton-startIcon": { color: "#1DA851" },
                                },
                            }}
                        >
                            واتساپ
                        </Button>
                    )}

                    {(markets?.tell || markets?.name) && (
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            size="small"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                    `${markets?.name ?? ""} - ${markets?.tell ?? ""} - ${
                                        markets?.address ?? ""
                                    }`
                                );
                            }}
                            sx={{
                                gap: 1,
                                borderColor: "#008ff8",
                                borderRadius: "12px",
                                color: "#008ff8",
                                fontWeight: 600,
                                "& .MuiButton-startIcon": { color: "#008ff8" },
                                "&:hover": {
                                    borderColor: "#0b74c9",
                                    backgroundColor: "rgba(0,143,248,0.08)",
                                    color: "#0b74c9",
                                    "& .MuiButton-startIcon": { color: "#0b74c9" },
                                },
                            }}
                        >
                            کپی اطلاعات
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        size="small"
                        sx={{
                            ml: "auto",
                            borderRadius: "12px",
                            fontWeight: 700,
                            px: 1.75,
                            gap: 1,
                            backgroundColor: "#FF1744",
                            "&:hover": { backgroundColor: "#e0143d" },
                        }}
                    >
                        ورود به فروشگاه
                    </Button>
                </Box>
            </Paper>
        </ButtonBase>
    );
}

function InlineBarcode({ code, height = 44, fontSize = 10 }) {
    const svgRef = useRef(null);
    useEffect(() => {
        if (!code || !svgRef.current) return;
        try {
            JsBarcode(svgRef.current, String(code), {
                format: "CODE128",
                displayValue: true,
                font: "IRANSans, Arial, sans-serif",
                fontSize,
                textMargin: 2,
                height,
                margin: 0,
                lineColor: "#111",
                background: "#fff",
            });
        } catch {}
    }, [code, height, fontSize]);

    return <svg ref={svgRef} style={{ display: "block" }} />;
}

function BarcodeTag({ code, price = null, title = "", date, rtl = true }) {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!code || !svgRef.current) return;
        try {
            JsBarcode(svgRef.current, String(code), {
                format: "CODE128",
                displayValue: true,
                font: "IRANSans, Arial, sans-serif",
                fontSize: 12,
                textMargin: 4,
                height: 48,
                margin: 0,
                lineColor: "#111",
                background: "#fff",
            });
        } catch (e) {
            console.error("Barcode render error", e);
        }
    }, [code]);

    const dateText =
        date ||
        new Date().toLocaleDateString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });

    return (
        <Box sx={{ px: 2, mt: 2 }}>
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    border: "1px solid rgba(0,0,0,0.12)",
                }}
            >
                {/* top strap */}
                <Box
                    sx={{
                        height: 6,
                        background:
                            "repeating-linear-gradient(90deg, #000, #000 6px, transparent 6px, transparent 12px)",
                        opacity: 0.25,
                    }}
                />

                {/* content row: info + (barcode + date) */}
                <Box
                    sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        direction: rtl ? "rtl" : "ltr",

                        // ✅ keep everything on one line; allow gentle horizontal scroll on very small screens
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    {/* left: tag info (shrinkable) */}
                    <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block", mb: 0.25 }}
                        >
                            بارکد
                        </Typography>

                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, letterSpacing: 0.2 }}
                            noWrap
                            title={String(code)}
                        >
                            {code}
                        </Typography>

                        {title ? (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 0.25,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                title={title}
                            >
                                {title}
                            </Typography>
                        ) : null}

                        {price != null ? (
                            <Chip
                                size="small"
                                label={`${Number(price).toLocaleString("fa-IR")} تومان`}
                                sx={{
                                    mt: 0.75,
                                    px: 1,
                                    height: 24,
                                    fontWeight: 700,
                                    color: "#0B7C3E",
                                    bgcolor: "rgba(16,185,129,.12)",
                                    borderRadius: 1.5,
                                }}
                            />
                        ) : null}
                    </Box>

                    {/* right: a non-wrapping group with barcode + date */}
                    <Box
                        sx={{
                            flex: "0 0 auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            whiteSpace: "nowrap", // ✅ ensure barcode & date stay on same line
                        }}
                    >
                        {/* barcode */}
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                bgcolor: "#fff",
                                border: "1px dashed rgba(0,0,0,0.2)",
                                borderRadius: 1,
                                p: 1,

                                // keep barcode reasonably narrow so it doesn't force wrapping
                                maxWidth: 260,
                            }}
                        >
                            <svg ref={svgRef} />
                        </Box>

                    </Box>
                </Box>

                {/* perforated line */}
                <Box
                    sx={{
                        px: 1.5,
                        pb: 1.5,
                        "&::before": {
                            content: '""',
                            display: "block",
                            width: "100%",
                            borderBottom: "1px dashed rgba(0,0,0,0.25)",
                        },
                    }}
                />

                {/* hole punch */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        boxShadow: "0 0 0 2px rgba(0,0,0,0.25) inset",
                    }}
                />
            </Paper>
        </Box>
    );
}


export default function ProductDetailComponent({ id }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [copied, setCopied] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [updateBasket, setUpdateBasket] = useState(0);

    /* ---------- fetch ---------- */
    useEffect(() => {
        if (!id) return;
        let alive = true;

        fetch(`${BASE_URL_API}product/${id}`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("pwa_token")}`,
                "Content-Type": "application/json",
            },
        })
            .then((r) => r.json())
            .then((json) => {
                if (alive) {
                    if (json?.code === 200) setData(json.data);
                    setLoading(false);
                }
            })
            .catch(() => alive && setLoading(false));

        return () => {
            alive = false;
        };
    }, [id]);

    /* ---------- share / copy ---------- */
    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: data?.title || "محصول", url });
                return;
            }
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        }
    };

    /* ---------- safe derived data (hooks before returns) ---------- */
    const step = data?.step ?? 0;
    const title = data?.title ?? "";
    const price = Number(data?.price ?? 0);
    const del_price = Number(data?.del_price ?? 0);
    const barcode = data?.barcode ?? "";
    const des = data?.des ?? "";
    const pictures = data?.pictures ?? [];
    const category = data?.category ?? null;
    const markets = data?.markets ?? null;

    const hasDiscount = del_price > 0;
    const finalPrice = hasDiscount ? price - del_price : price;

    const discountPct = useMemo(() => {
        if (!hasDiscount || !price) return 0;
        return clamp(Math.round((del_price / price) * 100), 1, 95);
    }, [hasDiscount, price, del_price]);

    /* ---------- early states ---------- */
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" height={32} sx={{ mt: 2, width: "60%" }} />
                <Skeleton variant="text" width="40%" />
                <Skeleton
                    variant="rectangular"
                    height={120}
                    sx={{ mt: 2, borderRadius: 2 }}
                />
            </Box>
        );
    }

    if (!data) return <Box sx={{ p: 3 }}>محصول پیدا نشد.</Box>;

    const inStock = Number(step) > 0;
    const isPayable = inStock && Number(finalPrice) > 0;

    const showToast = (message, type = "success") => {
        setSnackbarMessage(message);
        setSnackbarType(type);
        setOpenSnackbar(true);
    };

    /* ---------- add to cart ---------- */
    const onAddToCardClicked = () => {
        const findCardShop = CardShopRepository.getItem(id);
        if (findCardShop) {
            const totalEntity = parseInt(findCardShop.count);
            if (totalEntity < parseInt(step)) {
                if (finalPrice > 0) {
                    CardShopRepository.addCardShop(data);
                    setUpdateBasket(Math.random());
                    showToast("کالا با موفقیت به سبد خرید اضافه شد.");
                } else {
                    showToast("جهت خرید کالا تماس بگیرید.", "error");
                }
            } else {
                showToast("موجودی کالا به اتمام رسیده!", "error");
            }
        } else if (inStock) {
            if (finalPrice > 0) {
                CardShopRepository.addCardShop(data);
                setUpdateBasket(Math.random());
                showToast("کالا با موفقیت به سبد خرید اضافه شد.");
            } else {
                showToast("جهت خرید کالا تماس بگیرید.", "error");
            }
        } else {
            showToast("موجودی کالا به اتمام رسیده!", "error");
        }
    };

    /* ---------- UI ---------- */
    return (
        <>
            {/* Top AppBar (glass) */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: "rgba(255,255,255,0.55)",
                    backdropFilter: "blur(10px)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
            >
                <Toolbar sx={{ gap: 0.5 }}>
                    <IconButton onClick={() => navigate(-1)} size="large" edge="start">
                        <ArrowBackIosNewIcon sx={{ color: "text.primary" }} />
                    </IconButton>

                    <Typography
                        noWrap
                        sx={{ flexGrow: 1, fontWeight: 700, color: "text.primary", pr: 1 }}
                        title={title}
                    >
                        {title}
                    </Typography>

                    <IconButton onClick={handleShare} size="large">
                        <ShareIcon sx={{ color: "text.primary" }} />
                    </IconButton>

                    <IconButton size="large">
                        <BasketComponent updateBasket={updateBasket} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Content */}
            <Box sx={{ pt: 9.5, pb: 12, direction: "rtl" }}>
                {/* Hero: images with overlay badges */}
                <Box sx={{ borderRadius: 2, overflow: "hidden", mx: 2, position: "relative" }}>
                    {pictures?.length ? (
                        <AliceCarousel
                            autoPlay
                            infinite={pictures.length > 1}
                            autoPlayInterval={3500}
                            animationDuration={800}
                            disableButtonsControls
                            disableDotsControls={pictures.length === 1}
                        >
                            {pictures.map((pic, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        height: { xs: 260, sm: 320 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#fff",
                                    }}
                                >
                                    <img
                                        src={`${PRODUCT_BASE_URL_API}${pic.url}`}
                                        alt={`pic-${idx}`}
                                        style={{
                                            maxHeight: "100%",
                                            width: "100%",
                                            objectFit: "contain",
                                            display: "block",
                                        }}
                                    />
                                </Box>
                            ))}
                        </AliceCarousel>
                    ) : (
                        <Paper
                            sx={{
                                height: { xs: 260, sm: 320 },
                                backgroundImage: `url(/hamekara-logo.png)`,
                                backgroundSize: "contain",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                            }}
                        />
                    )}

                    {/* gradient bottom overlay */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: "auto 0 0 0",
                            height: 80,
                            background:
                                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.45) 100%)",
                            pointerEvents: "none",
                        }}
                    />

                    {/* corner badges */}
                    <Box sx={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 1 }}>
                        {hasDiscount && (
                            <Chip
                                icon={<LocalOfferOutlinedIcon />}
                                label={`${discountPct}% تخفیف`}
                                sx={pill("rgba(255, 23, 68, .9)", "#fff")}
                            />
                        )}
                        {inStock ? (
                            <Chip icon={<VerifiedIcon />} label="موجود" sx={pill("rgba(0, 200, 83, .95)", "#fff")} />
                        ) : (
                            <Chip label="ناموجود" sx={pill("rgba(244, 67, 54, .95)", "#fff")} />
                        )}
                    </Box>
                </Box>

                {/* Title + category crumbs + QR code at left */}
                <Box sx={{ px: 2, mt: 2 }}>
                    <Box
                        sx={{
                            direction: "rtl",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        {/* Right side: title + breadcrumbs */}
                        <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 800, mb: 0.5 }}
                                noWrap
                                title={title}
                            >
                                {title}
                            </Typography>

                            <Breadcrumbs
                                separator="›"
                                sx={{
                                    "& .MuiBreadcrumbs-ol": { justifyContent: "flex-start" },
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {category?.parent?.title && (
                                    <Chip
                                        size="small"
                                        label={category.parent.title}
                                        sx={{
                                            bgcolor: "grey.100",
                                            "&:hover": { bgcolor: "grey.200" },
                                            height: 24,
                                            fontSize: 12,
                                            maxWidth: 160,
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                        }}
                                    />
                                )}
                                {category?.title && (
                                    <Chip
                                        size="small"
                                        label={category.title}
                                        sx={{
                                            bgcolor: "grey.100",
                                            "&:hover": { bgcolor: "grey.200" },
                                            height: 24,
                                            fontSize: 12,
                                            maxWidth: 160,
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                        }}
                                    />
                                )}
                            </Breadcrumbs>
                        </Box>

                        {/* Left side: QR code (scan-ready) */}
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                p: 0.75,
                                borderRadius: 1.5,
                                border: "1px dashed rgba(0,0,0,0.25)",
                                bgcolor: "grey.200",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5, // spacing between QR and text
                            }}
                            aria-label="کد QR برای اسکن"
                            title="کد QR برای اسکن"
                        >
                            <QRCodeSVG
                                value={String(window.location.href)}
                                size={60}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="Q"
                                includeMargin={false}
                            />

                            {/* Scan me! label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 2,
                                }}
                            >
                                !Scan me
                            </Typography>
                        </Box>


                    </Box>
                </Box>


                {/* Price & stock card */}
                <Box sx={{ p: 2 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background:
                                "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,248,248,1) 100%)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Inventory2Icon fontSize="small" sx={{ color: inStock ? "success.main" : "error.main" }} />
                            <Typography
                                variant="body2"
                                sx={{ color: inStock ? "text.secondary" : "error.main", fontWeight: 600 }}
                            >
                                {inStock
                                    ? `موجودی: ${Number(step).toLocaleString("en", { maximumFractionDigits: 4 })}`
                                    : "تماس بگیرید"}
                            </Typography>
                        </Box>

                        {hasDiscount && (
                            <Typography variant="body2" sx={{ color: "text.disabled", textDecoration: "line-through" }}>
                                {formatPrice(price)} تومان
                            </Typography>
                        )}

                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, color: isPayable ? "success.main" : "warning.main", mt: 0.25 }}
                        >
                            {isPayable ? `${formatPrice(finalPrice)} تومان` : "قیمت: تماس بگیرید"}
                        </Typography>
                    </Paper>
                </Box>



                {/* ✨ Clickable Market card (routes to /market?id=...) */}
                {markets && (
                    <Box sx={{ px: 2, mt: 2 }}>
                        <MarketLinkCardInline markets={markets} />
                    </Box>
                )}

                {/* Description */}
                {des && (
                    <Box sx={{ px: 2, mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700, mb: 0.5 }}>
                            توضیحات
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: "pre-line",
                                color: "text.secondary",
                                lineHeight: 1.9,
                            }}
                        >
                            {des}
                        </Typography>
                    </Box>
                )}

                {/* Barcode */}
                {barcode && (
                    <BarcodeTag code={barcode} price={isPayable ? finalPrice : null} title={title} />
                )}



                {/* Spacing for bottom bar */}
                <Box sx={{ height: 80 }} />
            </Box>

            {/* Bottom action bar */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1,
                    background: "rgba(255,255,255,.9)",
                    backdropFilter: "blur(10px)",
                    borderTop: "1px solid rgba(0,0,0,.06)",
                }}
            >
                <Box sx={{ display: "flex", gap: 1.25 }}>
                    <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        onClick={onAddToCardClicked}
                        startIcon={<AddShoppingCartIcon />}
                        sx={{
                            gap: 1.2,
                            borderRadius: 2,
                            fontWeight: 800,
                            py: 1.25,
                            ...(isPayable
                                ? { backgroundColor: "#ff1744", "&:hover": { backgroundColor: "#e0143d" } }
                                : { backgroundColor: "warning.main" }),
                        }}
                    >
                        افزودن به سبد
                    </Button>

                    <Button variant="outlined" onClick={handleShare} sx={{ borderRadius: 2, minWidth: 56, px: 1.5 }}>
                        <ShareIcon />
                    </Button>
                </Box>
            </Box>

            {/* Snackbars */}
            <Snackbar
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity="success" variant="filled">
                    لینک محصول کپی شد!
                </Alert>
            </Snackbar>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={2000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarType} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
